import { useDispatch, useSelector } from 'react-redux';
import {
	getAdditionalServices,
	getAdditionalServicesLoading,
	getCreateAdditionalServiceLoading,
	getEmAdditionalServiceCancelLoading,
	getEmAdditionalServiceInvoiceLoading,
	getEmAdditionalServicesData,
	getEmAdditionalServicesLoading,
	getUpdateAdditionalServiceLoading,
} from 'redux/reducers/emManagement/selectors';
import { useEffect, useState } from 'react';
import {
	createEmAdditionalServiceRequest,
	emAdditionalServiceCancelRequest,
	emAdditionalServiceInvoiceRequest,
	emInvoicePdfRequest,
	getAdditionalServicesRequest,
	getEmAdditionalServicesRequest,
	updateEmAdditionalServiceRequest,
} from 'redux/reducers/emManagement/reducer';
import { PAGE_SIZE } from 'services/constants/env';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { TEmAdditionalService } from 'redux/reducers/emManagement/types';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { useParams } from 'react-router-dom';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { tableStructure } from './tableStructure';
import { EditParamsDialog } from './EditParamsDialog';
import { TBoxStructure } from '../../EasParamsBox/types';

export const EmAdditionalServices = () => {
	const { emId } = useParams<{ emId?: string }>();

	const dispatch = useDispatch();
	const emAdditionalServices = useSelector(getEmAdditionalServicesData);
	const emAdditionalServicesLoading = useSelector(getEmAdditionalServicesLoading);
	const additionalServices = useSelector(getAdditionalServices);
	const createAdditionalServiceRequestLoading = useSelector(getCreateAdditionalServiceLoading);
	const updateAdditionalServiceRequestLoading = useSelector(getUpdateAdditionalServiceLoading);
	const emAdditionalServiceInvoiceRequestLoading = useSelector(
		getEmAdditionalServiceInvoiceLoading,
	);
	const emAdditionalServiceCancelRequestLoading = useSelector(getEmAdditionalServiceCancelLoading);
	useEffect(() => {
		dispatch(
			getAdditionalServicesRequest({
				pageNumber: 1,
				pageSize: 1000,
				orderBy: [{ fieldName: 'id', direction: ESortDirection.ASC }],
			}),
		);
		if (emId) {
			dispatch(getEmAdditionalServicesRequest(Number(emId)));
		}
	}, [dispatch, emId]);

	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const handleEditClose = () => {
		setIsEditDialogOpen(false);
	};

	const [idToEdit, setIdToEdit] = useState<number | null>(null);
	const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
	const setCloseConfirmationDialog = () => {
		setOpenConfirmationDialog(false);
	};
	const handleConfirmationDialog = () => {
		if (!idToEdit) return;
		setOpenConfirmationDialog(false);
		dispatch(emAdditionalServiceInvoiceRequest({ id: idToEdit, emId: Number(emId) }));
	};
	const [openCancelConfirmationDialog, setOpenCancelConfirmationDialog] = useState(false);
	const setCloseCancelConfirmationDialog = () => {
		setOpenCancelConfirmationDialog(false);
	};
	const handleCancelConfirmationDialog = () => {
		if (!idToEdit) return;
		setOpenCancelConfirmationDialog(false);
		dispatch(emAdditionalServiceCancelRequest({ id: idToEdit, emId: Number(emId) }));
	};
	const handleSave = (data: Record<string, any>) => {
		console.log('idToEdit', idToEdit);
		if (idToEdit === null) {
			console.log('data', data);
			dispatch(
				createEmAdditionalServiceRequest({
					emId: Number(emId),
					serviceId: data.serviceId,
					name: data.name,
					price: Number(data.price),
					isRecurring: Boolean(data.isRecurring),
					servicePeriod: data.servicePeriod ? Number(data.servicePeriod) : null,
				}),
			);
		} else if (idToEdit) {
			dispatch(
				updateEmAdditionalServiceRequest({
					id: idToEdit,
					data: {
						emId: Number(emId),
						serviceId: data.serviceId,
						name: data.name,
						price: Number(data.price),
						isRecurring: Boolean(data.isRecurring),
						servicePeriod: data.servicePeriod ? Number(data.servicePeriod) : null,
					},
				}),
			);
		}
		handleEditClose();
	};

	const handleAction = (id: number, typeOfDialog: string) => {
		console.log('EmAdditionalServices - handleAction', typeOfDialog, id);
		switch (typeOfDialog) {
			case 'Edit':
				setIdToEdit(id);
				setIsEditDialogOpen(true);
				break;
			case 'Generate Invoice':
				setIdToEdit(id);
				setOpenConfirmationDialog(true);
				break;
			case 'Download Invoice':
				dispatch(
					emInvoicePdfRequest({
						emId: Number(emId),
						invoiceId:
							emAdditionalServices?.find((item) => item.id === id)?.includedIntoInvoice ?? 0,
					}),
				);
				break;
			case 'Cancellation of the recurring additional service':
				setIdToEdit(id);
				setOpenCancelConfirmationDialog(true);
				break;
			default:
				break;
		}
	};

	const tableStructureToBoxStructure = (
		tableStruc: TTableStructure<keyof TEmAdditionalService>[],
	) => {
		const boxStructure: TBoxStructure<TEmAdditionalService> = {
			body: [],
			header: [],
			title: idToEdit ? 'Edit Additional Service' : 'Add Additional Service',
		};
		tableStruc.forEach((item: TTableStructure<keyof TEmAdditionalService>) => {
			if (item?.editable === true && item.selector !== 'actions') {
				boxStructure.body.push({
					name: item.name,
					selector: item.selector,
					type: item.type,
					editable: true,
				});
			}
		});
		return boxStructure;
	};

	return (
		<Box>
			<EasTableWithoutFilter
				tableStructure={tableStructure}
				items={{
					data: emAdditionalServices ?? [],
					pageNumber: 1,
					pageSize: PAGE_SIZE,
					totalCount: emAdditionalServices?.length ?? 0,
				}}
				itemsIsLoading={
					emAdditionalServicesLoading ||
					createAdditionalServiceRequestLoading ||
					updateAdditionalServiceRequestLoading ||
					emAdditionalServiceInvoiceRequestLoading ||
					emAdditionalServiceCancelRequestLoading
				}
				handleAction={handleAction}
				skeletonRows={[1]}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						Additional Services
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						{(emAdditionalServicesLoading ||
							createAdditionalServiceRequestLoading ||
							updateAdditionalServiceRequestLoading ||
							emAdditionalServiceInvoiceRequestLoading ||
							emAdditionalServiceCancelRequestLoading) && <CircularProgress size={20} />}
						<Button
							variant="contained"
							color="primary"
							size="small"
							onClick={() => {
								setIdToEdit(null);
								setIsEditDialogOpen(true);
							}}
						>
							Add Service
						</Button>
					</Box>
				</Box>
			</EasTableWithoutFilter>
			<EditParamsDialog<TEmAdditionalService>
				open={isEditDialogOpen}
				setClose={handleEditClose}
				data={
					idToEdit ? (emAdditionalServices?.find((item) => item.id === idToEdit) ?? null) : null
				}
				boxStructure={tableStructureToBoxStructure(tableStructure)}
				onSave={handleSave}
				title={idToEdit ? 'Edit Additional Service' : 'Add Additional Service'}
				additionalServices={additionalServices?.data ?? null}
			/>
			<ConfirmationDialog
				open={openConfirmationDialog}
				setClose={setCloseConfirmationDialog}
				handleConfirm={handleConfirmationDialog}
				title="Generate Invoice"
				text="Are you sure you want to generate Invoice?"
			/>
			<ConfirmationDialog
				open={openCancelConfirmationDialog}
				setClose={setCloseCancelConfirmationDialog}
				handleConfirm={handleCancelConfirmationDialog}
				title="Cancel Additional Service"
				text="Are you sure you want to cancel this additional service?"
			/>
		</Box>
	);
};
